import {
  useMediaQuery,
  useTheme
} from '@mui/material';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import LinearProgress from '@mui/material/LinearProgress';
import Skeleton from '@mui/material/Skeleton';
import {
  DataGrid,
  DataGridProps,
  GridColDef,
  GridRowModel
} from '@mui/x-data-grid';
import {
  isNil,
  times
} from 'ramda';
import { i18n } from 'i18n';
import { useDebounce } from 'use-debounce';
import { getRandomInt } from 'utils';
import filterRowsBySearch from './filterRowsBySearch';

interface DataGridWithLoadingProps {
  loading: boolean;
  loadingNew: boolean;
  searchTerm: string | null;
  searchFields?: string[];
  pageSize: number;
  idProp: string;
}

interface MobileConfig {
  isVisible: boolean;
  width: number;
}
export type ResponsiveGridColDef = GridColDef & {
  mobileConfig?: MobileConfig;
}

export type ResponsiveDataGridProps = DataGridProps & {
  columns: ResponsiveGridColDef[];
}

const DataGridWithLoading = (
  { loadingNew, searchTerm, searchFields = [], loading, idProp, ...props }: ResponsiveDataGridProps & DataGridWithLoadingProps,
) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const isSearchingNew = searchTerm !== null && loadingNew;
  let { rows, columns } = props;

  const { pageSize } = props;

  if (isSearchingNew) {
    rows = filterRowsBySearch(rows ?? [], searchFields, searchTerm);
  }

  const hasLocalResultsForNewSearch = isSearchingNew && (rows ?? []).length;
  const [loadingNewDebounced] = useDebounce(loadingNew && !hasLocalResultsForNewSearch, 300);

  columns = columns.map((col: ResponsiveGridColDef) => {
    const processedColumn = {
      ...col,
      renderCell: (params: any) => {
        if (loadingNewDebounced) {
          return <Skeleton animation="wave" height={20} width={getRandomInt(80, 150)} />;
        }
        if (isNil(col.renderCell)) {
          return <>{params.formattedValue}</>;
        }
        return col.renderCell(params);
      },
    };
    if (isSmall && col.mobileConfig) {
      processedColumn.width = col.mobileConfig.width;
      processedColumn.flex = undefined;
    }
    return processedColumn;
  });

  if (loadingNewDebounced) {
    rows = [...(rows ?? []), ...times<any>((entityId) => ({ [idProp]: `${entityId}`, name: '' }), pageSize - (rows ?? []).length)];
  }

  const updateProps = {
    ...props,
    rows,
    columns,
    getRowId: (row: GridRowModel) => row[idProp],
  };

  return (
    (
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <DataGrid
          localeText={{ noRowsLabel: i18n._('No rows') }}
          {...updateProps}
          sx={{
            '& .MuiDataGrid-footerContainer': {
              overflowX: 'auto',
              minWidth: 0,
            },
            '& .MuiTablePagination-root': {
              overflowX: 'hidden',
            },
          }}
        />
        <Fade
          in={loading}
          style={{
            transitionDelay: loading ? '300ms' : '0ms',
          }}
          unmountOnExit
        >
          <Box
            sx={{
              position: 'absolute',
              top: 52,
              left: 0,
              width: '100%',
            }}
          >
            <LinearProgress />
          </Box>
        </Fade>
      </Box>
    )
  );
};

export default DataGridWithLoading;
